import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, MenuItem, Paper, Tab, Tabs, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { FdrService } from '../../services/fdr.service';
import { TreasuryService } from '../../services/treasury.service';
import { BorrowingService } from '../../services/borrowing.service';
import { BankGuaranteeService } from '../../services/bankGuarantee.service';
import { PERMISSIONS } from '../../constants/permissions';
import { Fdr, FdrInterestLine, FdrInterestRun, FdrStatus } from '../../interfaces/treasury.interface';
import { Facility } from '../../interfaces/borrowing.interface';
import { BankGuarantee } from '../../interfaces/bank/cheque.interface';
import { BankAccount } from '../../interfaces/bank/bankAccount.interface';
import { Segment } from '../../interfaces/accounting.interface';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { SearchableSelect } from '../common/SearchableSelect';
import { AccountAutocomplete } from '../common/AccountAutocomplete';
import FocusedRowChip from '../common/FocusedRowChip';
import { useFocusedRow } from '../../hooks/useFocusedRow';
import { firstOfMonthLocalIso, firstOfYearLocalIso, formatDate, toLocalIsoDate } from '../../utils/dateUtils';
import { formatMoney, humanizeEnum } from '../../utils/format';
import { helpElement } from '../../help/attrs';
import { FILTER_CELL, bankLabel, segmentLabel, useTreasuryRefData } from './treasuryRef';

const STATUSES: FdrStatus[] = ['ACTIVE', 'ENCASHED', 'RENEWED', 'CLOSED'];
const COLORS: Record<FdrStatus, 'success' | 'default' | 'info'> = { ACTIVE: 'success', ENCASHED: 'default', RENEWED: 'info', CLOSED: 'default' };

type Dlg =
  | { kind: 'create' }
  | { kind: 'lien' | 'release' | 'encash' | 'renew'; f: Fdr };

/** Help keys of the FDR dialog, by kind (literals so the help key scan sees them). */
const FDR_DLG_HELP: Record<Dlg['kind'], { paper: Record<string, string>; helpKey: string }> = {
  create: { paper: { 'data-help-page': 'bank.fdrCreate.page' }, helpKey: 'bank.fdrCreate.save' },
  lien: { paper: { 'data-help-page': 'bank.fdrLien.page' }, helpKey: 'bank.fdrLien.save' },
  release: { paper: { 'data-help-page': 'bank.fdrRelease.page' }, helpKey: 'bank.fdrRelease.save' },
  encash: { paper: { 'data-help-page': 'bank.fdrEncash.page' }, helpKey: 'bank.fdrEncash.save' },
  renew: { paper: { 'data-help-page': 'bank.fdrRenew.page' }, helpKey: 'bank.fdrRenew.save' },
};

/** Help attributes of the Notes / Remarks / Release note field, by kind. */
const FDR_NOTES_HELP: Record<Dlg['kind'], Record<string, string>> = {
  create: { 'data-help': 'bank.fdrCreate.field.remarks' },
  lien: { 'data-help': 'bank.fdrCreate.field.remarks' },
  release: { 'data-help': 'bank.fdrRelease.field.note' },
  encash: { 'data-help': 'bank.fdrClose.field.notes' },
  renew: { 'data-help': 'bank.fdrClose.field.notes' },
};

const Fdrs: React.FC = () => {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PERMISSIONS.BANK_ACCOUNT_MANAGE);
  const institutionId = AuthService.getSelectedInstitution()!;
  const ref = useTreasuryRefData(e => toast.showApiError(e, 'Failed to load reference data'));
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState<Fdr[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const { focusId, clear: clearFocus } = useFocusedRow();
  const [dlg, setDlg] = useState<Dlg | null>(null);
  const [saving, setSaving] = useState(false);
  const [placeWarn, setPlaceWarn] = useState<{ balance: number; limit: number; beyondLimit: boolean } | null>(null);
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [guarantees, setGuarantees] = useState<BankGuarantee[]>([]);

  // interest runs
  const [runs, setRuns] = useState<FdrInterestRun[]>([]);
  const [period, setPeriod] = useState(firstOfMonthLocalIso());
  const [lines, setLines] = useState<FdrInterestLine[]>([]);
  const [regFrom, setRegFrom] = useState(firstOfYearLocalIso());
  const [regTo, setRegTo] = useState(toLocalIsoDate());
  const [running, setRunning] = useState(false);
  const [voidTarget, setVoidTarget] = useState<FdrInterestLine | null>(null);
  const [voiding, setVoiding] = useState(false);

  // form
  const [fdrNumber, setFdrNumber] = useState('');
  const [bankName, setBankName] = useState('');
  const [funding, setFunding] = useState('');
  const [principal, setPrincipal] = useState('');
  const [rate, setRate] = useState('');
  const [dayCount, setDayCount] = useState('ACT_365');
  const [openDate, setOpenDate] = useState(toLocalIsoDate());
  const [tenor, setTenor] = useState('12');
  const [autoRenew, setAutoRenew] = useState(false);
  const [opening, setOpening] = useState(false);
  const [accruedThrough, setAccruedThrough] = useState('');
  const [accruedInterest, setAccruedInterest] = useState('');
  const [fdrAccountId, setFdrAccountId] = useState('');
  const [receivableId, setReceivableId] = useState('');
  const [incomeId, setIncomeId] = useState('');
  const [aitId, setAitId] = useState('');
  const [chargesId, setChargesId] = useState('');
  const [segmentId, setSegmentId] = useState('');
  const [lienNote, setLienNote] = useState('');
  const [lienFacilityId, setLienFacilityId] = useState('');
  const [lienGuaranteeId, setLienGuaranteeId] = useState('');
  const [date, setDate] = useState(toLocalIsoDate());
  const [gross, setGross] = useState('');
  const [ait, setAit] = useState('');
  const [charges, setCharges] = useState('');
  const [net, setNet] = useState('');
  const [bankId, setBankId] = useState('');
  const [capitalise, setCapitalise] = useState(true);
  const [newNumber, setNewNumber] = useState('');
  const [newRate, setNewRate] = useState('');
  const [newTenor, setNewTenor] = useState('');
  const [notes, setNotes] = useState('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setRows(await FdrService.list(institutionId, status || undefined));
    } catch (e) {
      toast.showApiError(e, 'Failed to load FDRs');
    } finally {
      setLoading(false);
    }
  }, [institutionId, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadRuns = useCallback(async () => {
    try {
      setRuns(await FdrService.listRuns(institutionId));
      setLines(await FdrService.interestRegister(institutionId, regFrom, regTo));
    } catch (e) {
      toast.showApiError(e, 'Failed to load interest runs');
    }
  }, [institutionId, regFrom, regTo]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === 1) loadRuns(); }, [tab, loadRuns]);
  useEffect(() => {
    (async () => {
      try {
        setFacilities(await BorrowingService.listFacilities(institutionId));
        setGuarantees(await BankGuaranteeService.list(institutionId, 'ISSUED'));
      } catch (e) {
        toast.showApiError(e, 'Failed to load lien options');
      }
    })();
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const openDlg = (d: Dlg) => {
    setDlg(d); setNotes(''); setDate(toLocalIsoDate()); setGross(''); setAit(''); setCharges(''); setNet(''); setBankId('');
    setCapitalise(true); setNewNumber(''); setNewRate(''); setNewTenor('');
    if (d.kind === 'create') {
      setFdrNumber(''); setBankName(''); setFunding(''); setPrincipal(''); setRate(''); setDayCount('ACT_365');
      setOpenDate(toLocalIsoDate()); setTenor('12'); setAutoRenew(false); setOpening(false); setAccruedThrough(''); setAccruedInterest('');
      setFdrAccountId(''); setReceivableId(''); setIncomeId(''); setAitId(''); setChargesId(''); setSegmentId('');
      setLienNote(''); setLienFacilityId(''); setLienGuaranteeId('');
    } else if (d.kind === 'lien') {
      setLienNote(d.f.lienNote ?? ''); setLienFacilityId(d.f.lienFacilityId ?? ''); setLienGuaranteeId(d.f.lienGuaranteeId ?? '');
    }
  };

  const valid = (() => {
    if (!dlg) return false;
    switch (dlg.kind) {
      case 'create':
        return !!fdrNumber.trim() && !!bankName.trim() && Number(principal) > 0 && Number(rate) > 0 && !!openDate
          && Number(tenor) > 0 && (opening || !!funding);
      case 'lien': return !!(lienNote.trim() || lienFacilityId || lienGuaranteeId);
      case 'release': return notes.trim().length > 0;
      case 'encash': return gross !== '' && Number(gross) >= 0;
      case 'renew': return gross !== '' && Number(gross) >= 0;
      default: return false;
    }
  })();

  const num = (s: string) => (s === '' ? undefined : Number(s));

  /** Warn (never block) when placing the FDR would take the funding bank account below zero. */
  const checkPlacementBalance = async (): Promise<boolean> => {
    if (!funding || opening) return true;
    try {
      const pos = await TreasuryService.cashPosition(institutionId, openDate || undefined);
      const row = pos.rows.find(r => r.bankAccountId === funding);
      if (!row) return true;
      const balance = Number(row.glBalance) - Number(principal);
      if (balance >= 0) return true;
      const limit = row.odLimit != null ? Number(row.odLimit) : 0;
      setPlaceWarn({ balance, limit, beyondLimit: -balance > limit });
      return false;
    } catch {
      return true; // the check is advisory; a caller without cash-position access just places the FDR
    }
  };

  const save = async (skipBalanceCheck = false) => {
    if (!dlg) return;
    try {
      setSaving(true);
      if (dlg.kind === 'create' && !skipBalanceCheck && !(await checkPlacementBalance())) {
        setSaving(false);
        return;
      }
      if (dlg.kind === 'create') {
        await FdrService.create({
          institutionId, fdrNumber: fdrNumber.trim(), bankName: bankName.trim(), fundingBankAccountId: funding || undefined,
          principal: Number(principal), interestRatePct: Number(rate), dayCount, openDate, tenorMonths: Number(tenor),
          autoRenew, opening, openingAccruedThrough: opening && accruedThrough ? accruedThrough : undefined,
          openingAccruedInterest: opening && accruedInterest !== '' ? Number(accruedInterest) : undefined,
          lienNote: lienNote.trim() || undefined, lienFacilityId: lienFacilityId || undefined,
          lienGuaranteeId: lienGuaranteeId || undefined, fdrAccountId: fdrAccountId || undefined,
          interestReceivableAccountId: receivableId || undefined, interestIncomeAccountId: incomeId || undefined,
          aitAccountId: aitId || undefined, chargesAccountId: chargesId || undefined, segmentId: segmentId || undefined,
          remarks: notes.trim() || undefined,
        });
      } else if (dlg.kind === 'lien') {
        await FdrService.setLien(dlg.f.id, {
          lienNote: lienNote.trim() || undefined, lienFacilityId: lienFacilityId || undefined,
          lienGuaranteeId: lienGuaranteeId || undefined,
        });
      } else if (dlg.kind === 'release') {
        await FdrService.releaseLien(dlg.f.id, notes.trim());
      } else if (dlg.kind === 'encash') {
        await FdrService.encash(dlg.f.id, {
          date: date || undefined, grossInterest: Number(gross), aitDeducted: num(ait), charges: num(charges),
          netReceived: num(net), bankAccountId: bankId || undefined, notes: notes.trim() || undefined,
        });
      } else {
        await FdrService.renew(dlg.f.id, {
          date: date || undefined, grossInterest: Number(gross), aitDeducted: num(ait), charges: num(charges), capitalise,
          newFdrNumber: newNumber.trim() || undefined, newInterestRatePct: num(newRate), newTenorMonths: num(newTenor),
          notes: notes.trim() || undefined,
        });
      }
      toast.showSuccess('Saved');
      setDlg(null);
      await load();
    } catch (e) {
      toast.showApiError(e, 'FDR action failed');
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    try {
      setRunning(true);
      await FdrService.runInterest(institutionId, period);
      toast.showSuccess('Interest run complete');
      await loadRuns();
    } catch (e) {
      toast.showApiError(e, 'Interest run failed');
    } finally {
      setRunning(false);
    }
  };

  const confirmVoidLine = async () => {
    if (!voidTarget || voiding) return;
    try {
      setVoiding(true);
      await FdrService.voidInterestLine(voidTarget.id);
      toast.showSuccess('Interest line voided');
      setVoidTarget(null);
      await loadRuns();
    } catch (e) {
      toast.showApiError(e, 'Void failed');
    } finally {
      setVoiding(false);
    }
  };

  const titles: Record<string, string> = {
    create: 'New FDR', lien: 'Set lien', release: 'Release lien', encash: 'Encash FDR', renew: 'Renew FDR',
  };

  return (
    <Box data-help-page="banking.fdr.page" sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Fixed deposits (FDR)</Typography>
        {canManage && tab === 0 && <Button data-help="banking.fdr.new" variant="contained" startIcon={<AddIcon />} onClick={() => openDlg({ kind: 'create' })}>New FDR</Button>}
      </Box>
      <Tabs data-help="banking.fdr.tabs" value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="FDR register" />
        <Tab label="Monthly interest" />
      </Tabs>

      {tab === 0 && <FocusedRowChip focusId={focusId} noun="FDR" onClear={clearFocus} />}
      {tab === 0 && (
        <>
          <Box data-help-section="banking.fdr.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField data-help="banking.fdr.statusFilter" select label="Status" value={status} onChange={e => setStatus(e.target.value)} sx={FILTER_CELL}>
              <MenuItem value="">All</MenuItem>
              {STATUSES.map(s => <MenuItem key={s} value={s}>{humanizeEnum(s)}</MenuItem>)}
            </TextField>
          </Box>
          {loading ? <CircularProgress /> : (
            <TableContainer data-help-section="banking.fdr.table" component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell data-help="banking.fdr.col.number">FDR no.</TableCell><TableCell data-help="banking.fdr.col.bank">Bank</TableCell><TableCell data-help="banking.fdr.col.principal" align="right">Principal</TableCell>
                    <TableCell data-help="banking.fdr.col.rate" align="right">Rate %</TableCell><TableCell data-help="banking.fdr.col.opened">Opened</TableCell><TableCell data-help="banking.fdr.col.matures">Matures</TableCell>
                    <TableCell data-help="banking.fdr.col.accrued" align="right">Accrued</TableCell><TableCell data-help="banking.fdr.col.status">Status</TableCell><TableCell data-help="banking.fdr.col.actions">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(focusId ? rows.filter(f => f.id === focusId) : rows).map(f => (
                    <TableRow key={f.id} hover>
                      <TableCell>{f.fdrNumber}</TableCell>
                      <TableCell>{f.bankName}</TableCell>
                      <TableCell align="right">{formatMoney(f.principal)}</TableCell>
                      <TableCell align="right">{f.interestRatePct}</TableCell>
                      <TableCell>
                        {formatDate(f.openDate)}
                        {f.openingAccruedInterest != null && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Opening accrued interest {formatMoney(f.openingAccruedInterest)}
                            {f.openingAccruedThrough ? ` through ${formatDate(f.openingAccruedThrough)}` : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {formatDate(f.maturityDate)}
                        {f.status === 'ACTIVE' && f.daysToMaturity <= 30 && (
                          <Chip size="small" sx={{ ml: 1 }} color={f.daysToMaturity < 0 ? 'error' : 'warning'}
                            label={f.daysToMaturity < 0 ? 'Matured' : `${f.daysToMaturity}d`} />
                        )}
                      </TableCell>
                      <TableCell align="right">{f.accruedInterest != null ? formatMoney(f.accruedInterest) : '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={humanizeEnum(f.status)} color={COLORS[f.status]} />
                        {f.liened && <Chip size="small" sx={{ ml: 0.5 }} color="warning" label="Liened" />}
                      </TableCell>
                      <TableCell>
                        {canManage && f.status === 'ACTIVE' && (
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                            <Button data-help="banking.fdr.action.lien" size="small" onClick={() => openDlg({ kind: 'lien', f })}>Set lien</Button>
                            {f.liened && <Button data-help="banking.fdr.action.releaseLien" size="small" onClick={() => openDlg({ kind: 'release', f })}>Release lien</Button>}
                            <Button data-help="banking.fdr.action.encash" size="small" disabled={f.liened} onClick={() => openDlg({ kind: 'encash', f })}>Encash</Button>
                            <Button data-help="banking.fdr.action.renew" size="small" onClick={() => openDlg({ kind: 'renew', f })}>Renew</Button>
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && <TableRow><TableCell colSpan={9} align="center">No FDRs.</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          <Box data-help-section="banking.fdr.runFilter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
            <TextField data-help="banking.fdr.period" label="Period (month start)" type="date" value={period} InputLabelProps={{ shrink: true }} sx={FILTER_CELL}
              onChange={e => setPeriod(e.target.value)} />
            {canManage && <Button data-help="banking.fdr.runNow" variant="contained" disabled={running || !period} onClick={runNow}>Run now</Button>}
          </Box>
          <Typography variant="h6" sx={{ mb: 1 }}>Runs</Typography>
          <TableContainer data-help-section="banking.fdr.runs" component={Paper} sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead><TableRow><TableCell data-help="banking.fdr.runs.col.period">Period</TableCell><TableCell data-help="banking.fdr.runs.col.status">Status</TableCell><TableCell data-help="banking.fdr.runs.col.trigger">Trigger</TableCell><TableCell data-help="banking.fdr.runs.col.interest" align="right">Interest</TableCell></TableRow></TableHead>
              <TableBody>
                {runs.map(r => (
                  <TableRow key={r.id}><TableCell>{formatDate(r.period)}</TableCell><TableCell>{humanizeEnum(r.status)}</TableCell>
                    <TableCell>{humanizeEnum(r.triggerSource)}</TableCell><TableCell align="right">{formatMoney(r.totalInterest)}</TableCell></TableRow>
                ))}
                {runs.length === 0 && <TableRow><TableCell colSpan={4} align="center">No runs yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="h6" sx={{ mb: 1 }}>Interest register</Typography>
          <Box data-help-section="banking.fdr.regFilter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <TextField data-help="banking.fdr.regFrom" label="From" type="date" value={regFrom} InputLabelProps={{ shrink: true }} sx={FILTER_CELL} onChange={e => setRegFrom(e.target.value)} />
            <TextField data-help="banking.fdr.regTo" label="To" type="date" value={regTo} InputLabelProps={{ shrink: true }} sx={FILTER_CELL} onChange={e => setRegTo(e.target.value)} />
          </Box>
          <TableContainer data-help-section="banking.fdr.register" component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow><TableCell data-help="banking.fdr.reg.col.fdr">FDR</TableCell><TableCell data-help="banking.fdr.reg.col.bank">Bank</TableCell><TableCell data-help="banking.fdr.reg.col.period">Period</TableCell><TableCell data-help="banking.fdr.reg.col.days" align="right">Days</TableCell>
                  <TableCell data-help="banking.fdr.reg.col.interest" align="right">Interest</TableCell><TableCell data-help="banking.fdr.reg.col.status">Status</TableCell><TableCell data-help="banking.fdr.reg.col.actions">Actions</TableCell></TableRow>
              </TableHead>
              <TableBody>
                {lines.map(l => (
                  <TableRow key={l.id}>
                    <TableCell>{l.fdrNumber}</TableCell><TableCell>{l.bankName}</TableCell><TableCell>{formatDate(l.period)}</TableCell>
                    <TableCell align="right">{l.days}</TableCell><TableCell align="right">{formatMoney(l.interestAmount)}</TableCell>
                    <TableCell>{humanizeEnum(l.status)}{l.error && <Typography variant="caption" color="error" display="block">{l.error}</Typography>}</TableCell>
                    <TableCell>{canManage && l.status === 'POSTED' && <Button data-help="banking.fdr.action.voidInterest" size="small" color="error" onClick={() => setVoidTarget(l)}>Void</Button>}</TableCell>
                  </TableRow>
                ))}
                {lines.length === 0 && <TableRow><TableCell colSpan={7} align="center">No interest lines in this range.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      <Dialog open={!!dlg} onClose={() => setDlg(null)} maxWidth="sm" fullWidth PaperProps={dlg ? FDR_DLG_HELP[dlg.kind].paper as any : undefined}>
        <DialogTitle>{dlg ? titles[dlg.kind] : ''}{dlg && dlg.kind !== 'create' ? ` ${dlg.f.fdrNumber}` : ''}</DialogTitle>
        {dlg && (
          <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }} data-help-section="bank.fdrDialog.section.body">
            {dlg.kind === 'create' && (
              <>
                <FormControlLabel data-help="bank.fdrCreate.field.opening" control={<Checkbox checked={opening} onChange={e => setOpening(e.target.checked)} />}
                  label="Opening balance (already placed; posts nothing)" />
                <TextField data-help="bank.fdrCreate.field.number" label="FDR number" value={fdrNumber} required fullWidth onChange={e => setFdrNumber(e.target.value)} />
                <TextField data-help="bank.fdrCreate.field.bankName" label="Bank name" value={bankName} required fullWidth onChange={e => setBankName(e.target.value)} />
                <SearchableSelect<BankAccount> data-help="bank.fdrCreate.field.funding" label={opening ? 'Funding bank account (optional)' : 'Funding bank account'}
                  options={ref.banks.filter(b => !b.currencyCode)} value={funding} required={!opening} getOptionLabel={bankLabel}
                  onChange={id => setFunding(id)} />
                <TextField data-help="bank.fdrCreate.field.principal" label="Principal" type="number" value={principal} required fullWidth onChange={e => setPrincipal(e.target.value)} />
                <TextField data-help="bank.fdrCreate.field.rate" label="Interest rate % p.a." type="number" value={rate} required fullWidth onChange={e => setRate(e.target.value)} />
                <TextField data-help="bank.fdrCreate.field.dayCount" select label="Day count" value={dayCount} fullWidth onChange={e => setDayCount(e.target.value)}>
                  <MenuItem value="ACT_365">ACT/365</MenuItem><MenuItem value="ACT_360">ACT/360</MenuItem>
                </TextField>
                <TextField data-help="bank.fdrCreate.field.openDate" label="Open date" type="date" value={openDate} required fullWidth InputLabelProps={{ shrink: true }} onChange={e => setOpenDate(e.target.value)} />
                <TextField data-help="bank.fdrCreate.field.tenor" label="Tenor (months)" type="number" value={tenor} required fullWidth onChange={e => setTenor(e.target.value)} />
                {opening && (
                  <TextField data-help="bank.fdrCreate.field.accruedThrough" label="Interest already accrued through (optional)" type="date" value={accruedThrough} fullWidth
                    InputLabelProps={{ shrink: true }} onChange={e => setAccruedThrough(e.target.value)} />
                )}
                {opening && (
                  <TextField data-help="bank.fdrCreate.field.accruedInterest" label="Interest already accrued on the ledger (BDT)" type="number" value={accruedInterest} fullWidth
                    helperText="Optional. Interest receivable already booked for this FDR in the opening ledger."
                    onChange={e => setAccruedInterest(e.target.value)} />
                )}
                <FormControlLabel data-help="bank.fdrCreate.field.autoRenew" control={<Checkbox checked={autoRenew} onChange={e => setAutoRenew(e.target.checked)} />} label="Auto-renew" />
                <AccountAutocomplete data-help="bank.fdrCreate.field.fdrAccount" accounts={ref.assets} value={fdrAccountId} label="FDR principal account (default from settings)" onChange={id => setFdrAccountId(id)} />
                <AccountAutocomplete data-help="bank.fdrCreate.field.receivableAccount" accounts={ref.assets} value={receivableId} label="Interest receivable account (default from settings)" onChange={id => setReceivableId(id)} />
                <AccountAutocomplete data-help="bank.fdrCreate.field.incomeAccount" accounts={ref.revenues} value={incomeId} label="Interest income account (default from settings)" onChange={id => setIncomeId(id)} />
                <AccountAutocomplete data-help="bank.fdrCreate.field.aitAccount" accounts={ref.assets} value={aitId} label="AIT account (default from settings)" onChange={id => setAitId(id)} />
                <AccountAutocomplete data-help="bank.fdrCreate.field.chargesAccount" accounts={ref.expenses} value={chargesId} label="Charges account (default from settings)" onChange={id => setChargesId(id)} />
                <SearchableSelect<Segment> data-help="bank.fdrCreate.field.segment" label="Segment" options={ref.segments} value={segmentId} getOptionLabel={segmentLabel} onChange={id => setSegmentId(id)} />
              </>
            )}
            {(dlg.kind === 'create' || dlg.kind === 'lien') && (
              <>
                <TextField data-help="bank.fdrLien.field.note" label="Lien note" value={lienNote} fullWidth onChange={e => setLienNote(e.target.value)} />
                <SearchableSelect<Facility> data-help="bank.fdrLien.field.facility" label="Lien to facility (optional)" options={facilities} value={lienFacilityId}
                  getOptionLabel={f => `${f.facilityNo} ${f.name}`} onChange={id => setLienFacilityId(id)} />
                <SearchableSelect<BankGuarantee> data-help="bank.fdrLien.field.guarantee" label="Lien to bank guarantee (optional)" options={guarantees} value={lienGuaranteeId}
                  getOptionLabel={g => `${g.guaranteeNumber} ${g.beneficiary}`} onChange={id => setLienGuaranteeId(id)} />
              </>
            )}
            {dlg.kind === 'release' && <Alert severity="info">Releasing the lien lets this FDR be encashed. A note is required.</Alert>}
            {(dlg.kind === 'encash' || dlg.kind === 'renew') && (
              <>
                <Alert severity="info">Enter what the bank actually credited and deducted.</Alert>
                {dlg.kind === 'renew' && dlg.f.liened && (
                  <Alert severity="warning">This FDR is liened. The lien carries over to the renewed FDR.</Alert>
                )}
                <TextField data-help="bank.fdrClose.field.date" label="Date" type="date" value={date} fullWidth InputLabelProps={{ shrink: true }} onChange={e => setDate(e.target.value)} />
                <TextField data-help="bank.fdrClose.field.gross" label="Gross interest credited" type="number" value={gross} required fullWidth onChange={e => setGross(e.target.value)} />
                <TextField data-help="bank.fdrClose.field.ait" label="AIT deducted" type="number" value={ait} fullWidth onChange={e => setAit(e.target.value)} />
                <TextField data-help="bank.fdrClose.field.charges" label="Excise duty / charges" type="number" value={charges} fullWidth onChange={e => setCharges(e.target.value)} />
              </>
            )}
            {dlg.kind === 'encash' && (
              <>
                <TextField data-help="bank.fdrEncash.field.net" label="Net received incl. principal (optional check)" type="number" value={net} fullWidth onChange={e => setNet(e.target.value)}
                  helperText="Total credited to the bank: principal + interest - AIT - charges" />
                <SearchableSelect<BankAccount> data-help="bank.fdrEncash.field.bank" label="Receiving bank account (default: funding account)" options={ref.banks.filter(b => !b.currencyCode)}
                  value={bankId} getOptionLabel={bankLabel} onChange={id => setBankId(id)} />
              </>
            )}
            {dlg.kind === 'renew' && (
              <>
                <FormControlLabel data-help="bank.fdrRenew.field.capitalise" control={<Checkbox checked={capitalise} onChange={e => setCapitalise(e.target.checked)} />}
                  label="Capitalise net interest into the new principal" />
                <TextField data-help="bank.fdrRenew.field.newNumber" label="New FDR number" value={newNumber} fullWidth onChange={e => setNewNumber(e.target.value)} />
                <TextField data-help="bank.fdrRenew.field.newRate" label="New rate % (blank = same)" type="number" value={newRate} fullWidth onChange={e => setNewRate(e.target.value)} />
                <TextField data-help="bank.fdrRenew.field.newTenor" label="New tenor months (blank = same)" type="number" value={newTenor} fullWidth onChange={e => setNewTenor(e.target.value)} />
              </>
            )}
            {dlg.kind !== 'lien' && (
              <TextField {...FDR_NOTES_HELP[dlg.kind]} label={dlg.kind === 'release' ? 'Release note' : 'Notes'} value={notes} required={dlg.kind === 'release'} fullWidth
                multiline minRows={2} onChange={e => setNotes(e.target.value)} />
            )}
          </DialogContent>
        )}
        <DialogActions>
          <Button data-help="bank.fdr.dlgClose" onClick={() => setDlg(null)}>Close</Button>
          <Button {...helpElement(dlg ? FDR_DLG_HELP[dlg.kind].helpKey : undefined)} variant="contained" disabled={saving || !valid} onClick={() => save()}>Save</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog open={!!placeWarn} title="Funding account will go negative" confirmColor="warning"
        confirmLabel="Place anyway"
        paperProps={{ 'data-help-page': 'bank.fdrPlaceWarn.page' }} confirmProps={{ 'data-help': 'bank.fdrPlaceWarn.confirm' }}
        cancelProps={{ 'data-help': 'bank.fdrPlaceWarn.cancel' }}
        message={placeWarn ? `Placing this FDR leaves the funding bank account at ${formatMoney(placeWarn.balance)}`
          + (placeWarn.limit > 0 ? ` (overdraft limit ${formatMoney(placeWarn.limit)}${placeWarn.beyondLimit ? ', exceeded' : ''}).` : '.')
          + ' You can still place it, but the account will be overdrawn.' : ''}
        submitting={saving} onCancel={() => setPlaceWarn(null)}
        onConfirm={() => { setPlaceWarn(null); void save(true); }} />
      <ConfirmDialog open={!!voidTarget} title="Void interest line" confirmColor="error" confirmLabel="Void"
        paperProps={{ 'data-help-page': 'bank.fdrVoidLine.page' }} confirmProps={{ 'data-help': 'bank.fdrVoidLine.confirm' }}
        cancelProps={{ 'data-help': 'bank.fdrVoidLine.cancel' }}
        message={voidTarget ? `Void the interest line of ${formatMoney(voidTarget.interestAmount)} for FDR ${voidTarget.fdrNumber}? Its journal entry will be reversed.` : ''}
        submitting={voiding} onCancel={() => setVoidTarget(null)} onConfirm={confirmVoidLine} />
    </Box>
  );
};

export default Fdrs;
